import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const Products = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedProduct.id) {
        await axios.put(`${API}/products/${selectedProduct.id}`, selectedProduct, { withCredentials: true });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, selectedProduct, { withCredentials: true });
        toast.success('Product created successfully');
      }
      fetchProducts();
      setSelectedProduct(null);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '800' }}>Manage Products</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedProduct({ category: '', services: [], materials: '' });
            setEditMode(true);
          }}
          data-testid="create-product-button"
        >
          <Plus size={16} />
          Add Product
        </button>
      </div>

      <div style={{ display: 'grid', gap: '20px' }}>
        {products.map(product => (
          <div key={product.id} className="card" data-testid={`product-card-${product.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>{product.category}</h3>
                <p style={{ fontSize: '14px', color: '#666' }}>{product.services.length} services</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                  onClick={() => {
                    setSelectedProduct(product);
                    setEditMode(false);
                  }}
                  data-testid={`view-product-${product.id}`}
                >
                  View
                </button>
                <button
                  className="btn btn-danger"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                  onClick={() => handleDelete(product.id)}
                  data-testid={`delete-product-${product.id}`}
                >
                  Delete
                </button>
              </div>
            </div>

            <div style={{ background: '#FAFAFA', padding: '12px', borderRadius: '8px' }}>
              <strong style={{ fontSize: '13px' }}>Materials:</strong>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>{product.materials}</p>
            </div>
          </div>
        ))}

        {products.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
            No products found. Add your first product!
          </div>
        )}
      </div>

      <Dialog open={!!selectedProduct} onOpenChange={() => { setSelectedProduct(null); setEditMode(false); }}>
        <DialogContent style={{ maxWidth: '900px' }}>
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Product' : 'Product Details'}</DialogTitle>
          </DialogHeader>

          {selectedProduct && (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {!editMode ? (
                <div>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Category</h3>
                    <div style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px' }}>
                      {selectedProduct.category}
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Services</h3>
                    {selectedProduct.services.map((service, idx) => (
                      <div key={idx} style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px', marginBottom: '12px' }}>
                        <div style={{ fontWeight: '600', marginBottom: '8px' }}>{service.name}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '14px' }}>
                          <div><strong>Price:</strong> ${service.price}</div>
                          <div><strong>Spiff:</strong> ${service.spiff}</div>
                          <div><strong>ID:</strong> {service.id}</div>
                        </div>
                        {service.notes && (
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                            <strong>Notes:</strong> {service.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Materials</h3>
                    <div style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px' }}>
                      {selectedProduct.materials}
                    </div>
                  </div>

                  <button
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => setEditMode(true)}
                  >
                    Edit Product
                  </button>
                </div>
              ) : (
                <div>
                  <div className="form-group">
                    <label className="form-label">Category Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={selectedProduct.category}
                      onChange={(e) => setSelectedProduct({ ...selectedProduct, category: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Materials</label>
                    <textarea
                      className="form-input"
                      value={selectedProduct.materials}
                      onChange={(e) => setSelectedProduct({ ...selectedProduct, materials: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <strong>Services (Edit as JSON)</strong>
                    <textarea
                      className="form-input"
                      value={JSON.stringify(selectedProduct.services, null, 2)}
                      onChange={(e) => {
                        try {
                          const services = JSON.parse(e.target.value);
                          setSelectedProduct({ ...selectedProduct, services });
                        } catch (err) {
                          // Invalid JSON, ignore
                        }
                      }}
                      rows={15}
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    />
                  </div>

                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSave}>
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
